import React, { useState, useRef, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
    Calendar,
    CalendarCheck,
    BarChart,
    Settings,
    Zap,
    GraduationCap,
    LayoutGrid,
    FileText,
    Package,
} from 'lucide-react';
import UserProfileDropdown from './UserProfileDropdown';
import { cn } from '@/utils/cn';
import { getThemeAssets } from '@/utils/theme';
import { getClientDeliverableStats } from '@/actions/client-portal';

const TopNavbar: React.FC = () => {
    const location = useLocation();
    const themeAssets = getThemeAssets();
    const [showAppSwitcher, setShowAppSwitcher] = useState(false);
    const appSwitcherRef = useRef<HTMLDivElement>(null);

    const [deliverablePendingCount, setDeliverablePendingCount] = useState(0);

    // Close app switcher when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (appSwitcherRef.current && !appSwitcherRef.current.contains(event.target as Node)) {
                setShowAppSwitcher(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Get client_id, sd_org_id, and is_client from localStorage
    const userStr = localStorage.getItem('crm_user');
    const user = userStr ? JSON.parse(userStr) : null;
    // Support multiple data structure paths (login vs /api/users/me response)
    const clientId = user?.data?.data?.client_id_fk || user?.data?.client_id_fk || user?.client_id_fk;
    const sdOrgId = user?.data?.data?.sd_org_id || user?.data?.data?.client?.sd_org_id || user?.data?.sd_org_id || user?.sd_org_id || user?.client?.sd_org_id;
    const isClient = user?.data?.data?.client?.is_client || user?.data?.client?.is_client || user?.client?.is_client || 0;

    // Determine leads route based on client_id (9 = admin)
    const isAdminClient = clientId == 9 || clientId === '9';
    const isClientUser = isClient === 1;
    const leadsRoute = isAdminClient ? 'leads_admin' : 'leads';

    // Fetch notification count for client users (pending reviews + unread admin comments)
    useEffect(() => {
        if (!isClientUser) return;
        getClientDeliverableStats().then((stats) => {
            setDeliverablePendingCount((stats.pending || 0) + (stats.unread_comment_count || 0));
        });
    }, [isClientUser]);

    // Function to check if a menu item is active
    const isActive = (href: string) => {
        const pathname = location.pathname;
        if (!pathname) return false;

        // Remove leading slash for comparison
        const cleanPath = pathname.replace(/^\//, '');

        // Exact match for some routes
        if (href === 'dashboard' && (cleanPath === 'dashboard' || cleanPath === '')) return true;
        // Leads menu should be active for leads, leads_admin, and pipeline pages
        if ((href === 'leads' || href === 'leads_admin') && (cleanPath === 'leads' || cleanPath === 'leads_admin' || cleanPath === 'pipeline')) return true;
        if (href === 'activities' && cleanPath === 'activities') return true;
        if (href === 'reports' && cleanPath === 'reports') return true;
        if (href === 'client-deliverables' && cleanPath.startsWith('client-deliverables')) return true;

        // Partial match for settings and admin routes
        if (href.startsWith('settings/') && cleanPath.startsWith('settings/')) {
            return cleanPath.includes(href.split('/')[1]);
        }
        if (href.startsWith('admin/') && cleanPath.startsWith('admin/')) {
            return true;
        }

        return false;
    };

    interface MenuItem {
        icon: React.ReactElement;
        label: string;
        href: string;
        isActive: boolean;
        badge?: number;
    }

    const menuItems: MenuItem[] = [
        { icon: <Calendar size={20} />, label: 'Leads', href: leadsRoute, isActive: isActive(leadsRoute) },
        { icon: <CalendarCheck size={20} />, label: 'Activities', href: 'activities', isActive: isActive('activities') },
        { icon: <Zap size={20} />, label: 'Marketing Automation', href: 'settings/drip-campaigns', isActive: isActive('settings/drip-campaigns') },
        { icon: <BarChart size={20} />, label: 'Reports', href: 'reports', isActive: isActive('reports') },
        // Deliverables menu - only for is_client=1 users
        ...(isClientUser ? [{ icon: <Package size={20} />, label: 'Deliverables', href: 'client-deliverables', isActive: isActive('client-deliverables'), badge: deliverablePendingCount }] : []),
        { icon: <Settings size={20} />, label: 'Settings', href: 'settings/profile', isActive: isActive('settings/profile') },
    ];

    return (
        <nav className="bg-brandColor text-lightColor shadow-md">
            <div className="flex items-center justify-between px-6 py-3">
                {/* App Switcher + Logo */}
                <div className="flex items-center space-x-8">
                    <div className="flex items-center space-x-3">
                        {/* App Switcher - Only for client_id = 9 */}
                        {isAdminClient && (
                            <div className="relative" ref={appSwitcherRef}>
                                <button
                                    onClick={() => setShowAppSwitcher(!showAppSwitcher)}
                                    className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                                    title="Switch Apps"
                                >
                                    <LayoutGrid size={20} className="text-white" />
                                </button>

                                {/* App Switcher Dropdown */}
                                {showAppSwitcher && (
                                    <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-[9999]">
                                        <div className="px-3 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider">
                                            Switch App
                                        </div>
                                        <div className="flex items-center gap-3 px-3 py-2.5 bg-brand/10">
                                            <div className="w-9 h-9 bg-brand rounded-lg flex items-center justify-center">
                                                <LayoutGrid size={18} className="text-white" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="font-medium text-gray-900 text-sm">CRM</div>
                                                <div className="text-xs text-gray-500">Lead Management</div>
                                            </div>
                                            <span className="text-brand text-xs font-medium">Active</span>
                                        </div>
                                        <Link
                                            to="/delivery-hub"
                                            onClick={() => setShowAppSwitcher(false)}
                                            className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="w-9 h-9 bg-brand rounded-lg flex items-center justify-center">
                                                <Package size={18} className="text-white" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="font-medium text-gray-900 text-sm">DeliveryHub</div>
                                                <div className="text-xs text-gray-500">Client Deliverables</div>
                                            </div>
                                        </Link>
                                        <a
                                            href={import.meta.env.VITE_BLOG_URL || 'http://localhost:5003'}
                                            onClick={() => setShowAppSwitcher(false)}
                                            className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="w-9 h-9 bg-brand rounded-lg flex items-center justify-center">
                                                <FileText size={18} className="text-white" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="font-medium text-gray-900 text-sm">Blog</div>
                                                <div className="text-xs text-gray-500">Content Management</div>
                                            </div>
                                        </a>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Logo */}
                        <Link to="/dashboard" className="flex items-center justify-center">
                            <img
                                src={themeAssets.loginLogo}
                                alt={`${themeAssets.brandName} Logo`}
                                className="h-8 object-contain"
                            />
                        </Link>
                    </div>

                    {/* Navigation Menu */}
                    <div className="flex items-center space-x-1">
                        {menuItems.map((item, index) => (
                            <Link
                                key={index}
                                to={`/${item.href}`}
                                className={cn(
                                    'relative flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200',
                                    item.isActive
                                        ? 'bg-white/20 text-white'
                                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                                )}
                            >
                                <div className="relative">
                                    {item.icon}
                                    {/* Badge indicator */}
                                    {item.badge !== undefined && item.badge > 0 && (
                                        <div className="absolute -top-2 -right-2 flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full">
                                            {item.badge > 99 ? '99+' : item.badge}
                                        </div>
                                    )}
                                </div>
                                <span className="text-sm font-medium">{item.label}</span>
                            </Link>
                        ))}
                    </div>
                </div>

                {/* Right Side - Admin and User Profile */}
                <div className="flex items-center space-x-3">
                    {/* Manage School - Only for users with sd_org_id (school admins) */}
                    {!!sdOrgId && sdOrgId !== 0 && sdOrgId !== '0' && clientId != 9 && clientId !== '9' && (
                        <>
                            <Link
                                to={`/manage-school/${sdOrgId}/basic`}
                                className={cn(
                                    'relative flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200',
                                    location.pathname.startsWith('/manage-school')
                                        ? 'bg-white/20 text-white'
                                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                                )}
                            >
                                <GraduationCap size={20} />
                                <span className="text-sm font-medium">Manage School</span>
                            </Link>

                            {/* Parent App nav link — hidden for now
                            <Link
                                to="/erp/dashboard"
                                className={cn(
                                    'relative flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200',
                                    location.pathname.startsWith('/erp')
                                        ? 'bg-white/20 text-white'
                                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                                )}
                            >
                                <Users size={20} />
                                <span className="text-sm font-medium">Parent App</span>
                            </Link>
                            */}
                        </>
                    )}

                    {/* Admin Menu - Only for client_id = 9 */}
                    {(clientId == 9 || clientId === '9') && (
                        <Link
                            to="/admin/schools"
                            className={cn(
                                'relative flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200',
                                isActive('admin/schools')
                                    ? 'bg-white/20 text-white'
                                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                            )}
                        >
                            <Settings size={20} />
                            <span className="text-sm font-medium">Admin</span>
                        </Link>
                    )}

                    <UserProfileDropdown />
                </div>
            </div>
        </nav>
    );
};

export default TopNavbar;
